import { Fragment, useEffect, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import swal from "sweetalert";
import api from "../../../api";
import PageTitle from "../../layouts/PageTitle";
import FilePreview from "../Common/FilePreview";

const HandoverEdit = () => {
 const { id } = useParams();
 const navigate = useNavigate();
 const [searchParams] = useSearchParams();

const workOrderFromUrl = searchParams.get("workOrder") || "";
const projectFromUrl = searchParams.get("project") || "";

 const [workOrders, setWorkOrders] = useState([]);
 const [boqItems, setBoqItems] = useState([]);
 const [billings, setBillings] = useState([]);
 const [loading, setLoading] = useState(false);
 const [fetching, setFetching] = useState(true);

 const [handoverDocsPreview, setHandoverDocsPreview] = useState([]);
 const [clientSignPreview, setClientSignPreview] = useState(null);

 // FORM
const [form, setForm] = useState({
 hoNumber: "",
 workOrder: "", // [OK] correct
 boqSelected: [],
 handoverDocuments: [],
 checklist: [],
 clientSignature: null,
 billingLinked: "",
 status: "Pending",
});

 // TEMP BOQ
 const [boqItemTemp, setBoqItemTemp] = useState({
 boqId: "",
 description: "",
 quantity: "",
 });

 // TEMP CHECKLIST
 const [checklistItem, setChecklistItem] = useState({
 item: "",
 status: "Pending",
 remarks: "",
 });

 // ========================================
 // LOAD HANDOVER + REQUIRED DATA
 // ========================================
 useEffect(() => {
 loadInitial();
 }, [id]);

 const loadInitial = async () => {
 try {
 const res = await api.get(`/handover/${id}`);

 if (!res.data.success) {
 swal("Error", "Handover not found", "error");
 navigate(
 workOrderFromUrl
 ? `/handover?${projectFromUrl ? `project=${projectFromUrl}&` : ""}workOrder=${workOrderFromUrl}`
 : "/handover"
);
 }

 const h = res.data.data;

 // ====== SET FORM ======
 const boqWithIds = (h.boqSelected || []).map(item => ({
 ...item,
 boqItemId: item.boqItemId || item.boqId
 }));

 setForm({
 hoNumber: h.hoNumber,
 workOrder: h.workOrder?._id || "",
 boqSelected: boqWithIds,
 handoverDocuments: Array.isArray(h.handoverDocuments)
 ? h.handoverDocuments
 : h.handoverDocuments
 ? [h.handoverDocuments]
 : [],
 checklist: h.checklist || [],
 clientSignature: h.clientSignature || null,
 billingLinked: h.billingLinked?._id || "",
 status: h.status,
});


 // ====== BOQ ITEMS FROM PROJECT ======
 if (h.workOrder?.boqItems) {
 setBoqItems(h.workOrder.boqItems);
} else {
 setBoqItems([]);
}


 // ====== HANDOVER DOC PREVIEW ======
 if (h.handoverDocuments) {
 const docs = Array.isArray(h.handoverDocuments)
 ? h.handoverDocuments
 : [h.handoverDocuments];

 setHandoverDocsPreview(
 docs.map((d) => ({ name: d.fileName, size: "Uploaded" }))
 );
 }

 // ====== SIGNATURE PREVIEW ======
 if (h.clientSignature) {
 setClientSignPreview(h.clientSignature.fileUrl);
 }

 // Load related data
 fetchWorkOrders();
 fetchBillings();

 if (h.workOrder?._id) {
 fetchBOQ(h.workOrder._id, id);
 }
 } catch (err) {
 swal("Error", "Could not fetch Handover", "error");
 navigate("/handover");
 } finally {
 setFetching(false);
 }
 };
 useEffect(() => {
 if (workOrderFromUrl && !form.workOrder) {
 setForm((prev) => ({
 ...prev,
 workOrder: workOrderFromUrl,
 }));
 }
}, [workOrderFromUrl]);

 // ========================================
 // FETCH WORK ORDERS
 // ========================================
 const fetchWorkOrders = async () => {
 try {
 const res = await api.get("/work-orders?limit=1000");
 if (res.data.success) setWorkOrders(res.data.data);
 } catch {
 swal("Error", "Failed to load Work Orders", "error");
 }
 };

 // ========================================
 // FETCH BILLINGS
 // ========================================
 const fetchBillings = async () => {
 try {
 const res = await api.get("/billing");
 if (res.data.success) setBillings(res.data.data);
 } catch {}
 };

 const fetchBOQ = async (workOrderId, excludeHandoverId = null) => {
 try {
 const url = excludeHandoverId 
 ? `/work-orders/${workOrderId}/boq?excludeHandoverId=${excludeHandoverId}`
 : `/work-orders/${workOrderId}/boq`;
 const res = await api.get(url);
 if (res.data.success) setBoqItems(res.data.data);
 else setBoqItems([]);
 } catch (error) {
 console.error("Failed to load BOQ list", error);
 }
 };

 // ========================================
 // INPUT HANDLERS
 // ========================================
 const handleChange = (e) =>
 setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

 const handleChecklistChange = (e) =>
 setChecklistItem((prev) => ({ ...prev, [e.target.name]: e.target.value }));

 const handleBOQChange = (e) =>
 setBoqItemTemp((prev) => ({ ...prev, [e.target.name]: e.target.value }));

 // ========================================
 // ADD CHECKLIST
 // ========================================
 const addChecklistItem = () => {
 if (!checklistItem.item.trim())
 return swal("Warning", "Checklist item required", "warning");

 setForm((prev) => ({
 ...prev,
 checklist: [...prev.checklist, checklistItem],
 }));

 setChecklistItem({ item: "", status: "Pending", remarks: "" });
 };

 const removeChecklistItem = (i) => {
 setForm((prev) => ({
 ...prev,
 checklist: prev.checklist.filter((_, idx) => idx !== i),
 }));
 };

 // ========================================
 // ADD BOQ ITEM
 // ========================================
 const addBOQItem = () => {
 if (!boqItemTemp.boqId)
 return swal("Warning", "Select a BOQ item", "warning");

 if (!boqItemTemp.description.trim())
 return swal("Warning", "Work Description required", "warning");

 if (!boqItemTemp.quantity || boqItemTemp.quantity <= 0)
 return swal("Warning", "Enter valid quantity", "warning");

 const selected = boqItems.find((b) => b._id === boqItemTemp.boqId);

 if (selected && Number(boqItemTemp.quantity) > Number(selected.availableHandoverQuantity)) {
 swal({
 title: "Warning",
 text: `Quantity entered (${boqItemTemp.quantity}) exceeds remaining quantity (${selected.availableHandoverQuantity}). Do you want to proceed?`,
 icon: "warning",
 buttons: true,
 dangerMode: true,
 }).then((willAdd) => {
 if (willAdd) {
 performAddBOQItem(selected);
 }
 });
 return;
 }

 performAddBOQItem(selected);
 };

 const performAddBOQItem = (selected) => {
 setForm((prev) => ({
 ...prev,
 boqSelected: [
 ...prev.boqSelected,
 {
 ...boqItemTemp,
 boqItemId: boqItemTemp.boqId,
 itemName: `[WLI-${selected?.wlino || "-"}] ${selected?.itemName}`,
 unit: selected?.unit,
 },
 ],
 }));

 setBoqItemTemp({ boqId: "", description: "", quantity: "" });
 };

 const removeBOQItem = (i) => {
 setForm((prev) => ({
 ...prev,
 boqSelected: prev.boqSelected.filter((_, idx) => idx !== i),
 }));
 };

 // ========================================
 // FILE HANDLING
 // ========================================
 const fileToBase64 = (file) =>
 new Promise((resolve) => {
 const reader = new FileReader();
 reader.onload = () => resolve(reader.result);
 reader.readAsDataURL(file);
 });

 const handleFile = async (e, field) => {
 if (field === "handoverDocuments") {
 const files = Array.from(e.target.files);

 const converted = await Promise.all(
 files.map(async (f) => ({
 fileName: f.name,
 fileUrl: await fileToBase64(f),
 documentType: f.name.split(".").pop().toUpperCase(),
 }))
 );

 setForm((prev) => ({
 ...prev,
 handoverDocuments: [...prev.handoverDocuments, ...converted],
 }));

 setHandoverDocsPreview((prev) => [
 ...prev,
 ...files.map((f) => ({ name: f.name, size: f.size })),
 ]);
 } else {
 const file = e.target.files[0];
 if (!file) return;

 const base64 = await fileToBase64(file);

 setForm((prev) => ({
 ...prev,
 clientSignature: { fileName: file.name, fileUrl: base64 },
 }));
 setClientSignPreview(base64);
 }
 };

 const removeHandoverDoc = (i) => {
 setForm((prev) => ({
 ...prev,
 handoverDocuments: prev.handoverDocuments.filter((_, idx) => idx !== i),
 }));
 setHandoverDocsPreview((prev) =>
 prev.filter((_, idx) => idx !== i)
 );
 };

 const removeClientSign = () => {
 setForm((prev) => ({ ...prev, clientSignature: null }));
 setClientSignPreview(null);
 };

 // ========================================
 // SUBMIT UPDATE
 // ========================================
const handleSubmit = async (e) => {
 e.preventDefault();

 if (!form.hoNumber.trim())
 return swal("Warning", "HO Number required", "warning");

 if (!form.workOrder)
 return swal("Warning", "Work Order required", "warning");


 setLoading(true);

 try {
 // Convert empty billingLinked to null
 const payload = {
 hoNumber: form.hoNumber,
 workOrder: form.workOrder, // [OK] required
 boqSelected: form.boqSelected,
 checklist: form.checklist,
 handoverDocuments: form.handoverDocuments,
 clientSignature: form.clientSignature,
 billingLinked: form.billingLinked || null,
 status: form.status,
};


 const res = await api.patch(`/handover/${id}`, payload);

 if (res.data.success) {
 swal("Success", "Handover updated successfully!", "success");
 
 const filterQuery = [
 projectFromUrl ? `project=${projectFromUrl}` : "",
 workOrderFromUrl ? `workOrder=${workOrderFromUrl}` : ""
 ].filter(Boolean).join("&");

 navigate(filterQuery ? `/handover?${filterQuery}` : "/handover");
 } else {
 swal("Error", res.data.message, "error");
 }
 } catch (err) {
 swal("Error", err.response?.data?.message || err.message, "error");
 }

 setLoading(false);
};


 if (fetching) {
 return (
 <Fragment>
 <PageTitle activeMenu="Edit Handover" motherMenu="ITC" />
 <div className="text-center py-5">
 <div className="spinner-border text-primary"></div>
 <p className="mt-3">Loading...</p>
 </div>
 </Fragment>
 );
 }

 // ========================================
 // UI START
 // ========================================
 return (
 <Fragment>
 <PageTitle activeMenu="Edit Handover" motherMenu="ITC" />

 <Row>
 <Col lg={12}>
 <Card>
 <Card.Header className="d-flex justify-content-between align-items-center">
 <Card.Title>Edit Handover</Card.Title>

 <button
 type="button"
 className="btn btn-secondary"
 onClick={() =>
 navigate(
 workOrderFromUrl
 ? `/handover?${projectFromUrl ? `project=${projectFromUrl}&` : ""}workOrder=${workOrderFromUrl}`
 : "/handover"
 )
}
 >
 <i className="fas fa-arrow-left me-2"></i> Back
 </button>
 </Card.Header>

 <Card.Body>
 <form onSubmit={handleSubmit}>
 
 {/* ========================= */}
 {/* HO NUMBER + PROJECT */}
 {/* ========================= */}
 <Row>
 <Col md={6}>
 <label className="form-label">HO Number *</label>
 <input
 type="text"
 className="form-control"
 name="hoNumber"
 value={form.hoNumber}
 onChange={handleChange}
 />
 </Col>

 <Col md={6}>
 <label className="form-label">Work Order *</label>
<select
 name="workOrder"
 className="form-select"
 value={form.workOrder}
 onChange={handleChange}
>
 <option value="">Select Work Order</option>
 {workOrders.map((wo) => (
 <option key={wo._id} value={wo._id}>
 {wo.woNo}{wo.label ? ` – ${wo.label}` : ""}
 </option>
 ))}
</select>

 </Col>
 </Row>

 {/* ========================= */}
 {/* STATUS + BILLING */}
 {/* ========================= */}
 <Row className="mt-3">
 <Col md={6}>
 <label className="form-label">Status</label>
 <select
 name="status"
 className="form-select"
 value={form.status}
 onChange={handleChange}
 >
 <option value="Pending">Pending</option>
 </select>
 </Col>

 <Col md={6}>
 <label className="form-label">Billing Linked</label>
 <select
 name="billingLinked"
 className="form-select"
 value={form.billingLinked}
 onChange={handleChange}
 >
 <option value="">Select Billing</option>
 {billings.map((b) => (
 <option key={b._id} value={b._id}>
 {b.billingNumber || b.invoiceNumber}
 </option>
 ))}
 </select>
 </Col>
 </Row>

 {/* ========================= */}
 {/* BOQ SECTION */}
 {/* ========================= */}
 <div className="mt-4">
 <label className="fw-bold">BOQ Work Done</label>
 <Card className="border">
 <Card.Body>
 <Row className="g-2 mb-3">
 <Col md={3}>
 <select
 name="boqId"
 className="form-select"
 value={boqItemTemp.boqId}
 onChange={handleBOQChange}
 >
 <option value="">Select BOQ</option>
 {boqItems.map((b) => (
 <option key={b._id} value={b._id}>
 [WLI-{b.wlino || "-"}] {b.itemName} ({b.unit})
 </option>
 ))}
 </select>
 {boqItemTemp.boqId && (
 <div className="mt-1 small text-muted">
 {(() => {
 const selected = boqItems.find(
 (b) => b._id === boqItemTemp.boqId
 );
 return selected ? (
 <>
 <span className="me-2">
 Total: <strong>{selected.quantity}</strong>
 </span>
 <span>
 Used: <strong>{selected.handoverUsedQuantity || 0}</strong>
 </span>
 <div className="text-primary mt-1">
 Remaining: <strong>{selected.availableHandoverQuantity || 0}</strong>
 </div>
 </>
 ) : null;
 })()}
 </div>
 )}
 </Col>

 <Col md={5}>
 <input
 type="text"
 name="description"
 className="form-control"
 placeholder="Description"
 value={boqItemTemp.description}
 onChange={handleBOQChange}
 />
 </Col>

 <Col md={2}>
 <input
 type="number"
 name="quantity"
 className="form-control"
 placeholder="Qty"
 value={boqItemTemp.quantity}
 onChange={handleBOQChange}
 />
 </Col>

 <Col md={2}>
 <button
 type="button"
 className="btn btn-primary w-100"
 onClick={addBOQItem}
 >
 <i className="fas fa-plus me-1"></i>Add
 </button>
 </Col>
 </Row>

 {form.boqSelected.length > 0 ? (
 <table className="table table-bordered table-sm">
 <thead className="table-light">
 <tr>
 <th>BOQ Item</th>
 <th>Description</th>
 <th>Qty</th>
 <th>Unit</th>
 <th>Action</th>
 </tr>
 </thead>

 <tbody>
 {form.boqSelected.map((item, i) => (
 <tr key={i}>
 <td>{item.itemName || item.boqName}</td>

 <td>{item.description}</td>
 <td>{item.quantity}</td>
 <td>{item.unit}</td>
 <td>
 <button
 type="button"
 className="btn btn-danger btn-sm"
 onClick={() => removeBOQItem(i)}
 >
 <i className="fas fa-trash"></i>
 </button>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 ) : (
 <p className="text-muted text-center m-0">No BOQ added yet</p>
 )}
 </Card.Body>
 </Card>
 </div>

 {/* ========================= */}
 {/* CHECKLIST */}
 {/* ========================= */}
 <div className="mt-4">
 <label className="fw-bold">Checklist</label>
 <Card className="border">
 <Card.Body>
 <Row className="g-2 mb-3">
 <Col md={4}>
 <input
 name="item"
 className="form-control"
 placeholder="Checklist Item"
 value={checklistItem.item}
 onChange={handleChecklistChange}
 />
 </Col>

 <Col md={3}>
 <select
 name="status"
 className="form-select"
 value={checklistItem.status}
 onChange={handleChecklistChange}
 >
 <option>Pending</option>
 <option>Completed</option>
 <option>N/A</option>
 </select>
 </Col>

 <Col md={3}>
 <input
 name="remarks"
 className="form-control"
 placeholder="Remarks"
 value={checklistItem.remarks}
 onChange={handleChecklistChange}
 />
 </Col>

 <Col md={2}>
 <button
 type="button"
 className="btn btn-primary w-100"
 onClick={addChecklistItem}
 >
 <i className="fas fa-plus me-1"></i>Add
 </button>
 </Col>
 </Row>

 {form.checklist.length > 0 ? (
 <table className="table table-bordered table-sm">
 <thead>
 <tr>
 <th>Item</th>
 <th>Status</th>
 <th>Remarks</th>
 <th>Action</th>
 </tr>
 </thead>

 <tbody>
 {form.checklist.map((item, i) => (
 <tr key={i}>
 <td>{item.item}</td>
 <td>{item.status}</td>
 <td>{item.remarks}</td>
 <td>
 <button
 className="btn btn-danger btn-sm"
 onClick={() => removeChecklistItem(i)}
 >
 <i className="fas fa-trash"></i>
 </button>
 </td>
 </tr>))}
 </tbody>
 </table>
 ) : (
 <p className="text-muted text-center m-0">
 No checklist items added yet
 </p>
 )}
 </Card.Body>
 </Card>
 </div>

 {/* ========================= */}
 {/* FILE UPLOADS */}
 {/* ========================= */}
 <div className="mt-4">
 <label className="fw-bold">Handover Documents</label>

 <input
 type="file"
 className="form-control"
 multiple
 accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls"
 onChange={(e) => handleFile(e, "handoverDocuments")}
 />

 {form.handoverDocuments.length > 0 && (
 <div className="mt-3 d-flex flex-wrap gap-2">
 {form.handoverDocuments.map((doc, i) => (
 <FilePreview
 key={i}
 file={doc}
 onRemove={() => removeHandoverDoc(i)}
 />
 ))}
 </div>
 )}
 </div>

 {/* ========================= */}
 {/* CLIENT SIGNATURE */}
 {/* ========================= */}
 <div className="mt-4">
 <label className="fw-bold">Client Signature</label>

 <input
 type="file"
 className="form-control"
 accept="image/*"
 onChange={(e) => handleFile(e, "clientSignature")}
 />

 {form.clientSignature && (
 <div className="mt-3">
 <FilePreview
 file={form.clientSignature}
 onRemove={removeClientSign}
 />
 </div>
 )}
 </div>

 {/* ========================= */}
 {/* SUBMIT */}
 {/* ========================= */}
 <div className="mt-4 d-flex gap-2">
 <button className="btn btn-primary" disabled={loading}>
 {loading ? "Updating..." : "Update Handover"}
 </button>

 <button
 type="button"
 className="btn btn-danger"
 disabled={loading}
 onClick={() =>
 navigate(
 workOrderFromUrl
 ? `/handover?${projectFromUrl ? `project=${projectFromUrl}&` : ""}workOrder=${workOrderFromUrl}`
 : "/handover"
 )
}
 >
 Cancel
 </button>
 </div>

 </form>
 </Card.Body>
 </Card>
 </Col>
 </Row>
 </Fragment>
 );
};

export default HandoverEdit;
